import React, { useEffect, useMemo, useState } from 'react';
import { apiURL2 } from '@config';

// Short-lived reproduction page for the Mozio whitelabel currency bug.
// Delete this file (and the corresponding route in App.jsx + the backend's
// /api/v2/mozio-demo/sso-token endpoint) once Mozio acknowledges the issue.

const WHITELABEL_BASE = 'https://aotravelconnect.mozio.com';

const FIXED_TRIP = {
    pickup: 'Sydney Airport - Mascot NSW 2020, Australia',
    dropoff: 'Hilton Sydney, 488 George St, Sydney NSW 2000, Australia',
    pickupDatetime: '06/15/2026 09:00 am',
    numPassengers: '2',
    mode: 'one_way',
};

const CURRENCIES = ['AUD', 'USD', 'CNY', 'GBP', 'EUR'];
const LANGUAGES = ['en-us', 'zh-cn', 'en-gb', 'fr-fr', 'de-de', 'es-es', 'ja-jp', 'pt-br'];

function buildDeeplink({ ssoToken, currency, language, partnerTrackingId }) {
    const u = new URL(`${WHITELABEL_BASE}/${language}/search/`);
    const q = u.searchParams;
    if (ssoToken) q.set('sso_token', ssoToken);
    q.set('mode', FIXED_TRIP.mode);
    q.set('num_passengers', FIXED_TRIP.numPassengers);
    q.set('currency', currency);
    q.set('start_address', FIXED_TRIP.pickup);
    q.set('end_address', FIXED_TRIP.dropoff);
    q.set('pickup_datetime', FIXED_TRIP.pickupDatetime);
    if (partnerTrackingId) q.set('partner_tracking_id', partnerTrackingId);
    q.set('ref', 'aotravelconnect');
    q.set('ta', '1');
    // Local dev provisions sherryfq_@hotmail.com against Mozio's testing API;
    // production uses the real Mozio account, so testing_api must be absent.
    if (process.env.NODE_ENV !== 'production') {
        q.set('testing_api', '1');
    }
    return u.toString();
}

const styles = {
    page: {
        fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        color: '#1f2937',
        background: '#f9fafb',
        minHeight: '100vh',
        padding: '32px 24px',
    },
    container: { maxWidth: 960, margin: '0 auto' },
    h1: { fontSize: 24, fontWeight: 600, margin: '0 0 8px' },
    intro: { fontSize: 14, color: '#4b5563', lineHeight: 1.5, margin: '0 0 24px' },
    section: {
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        padding: 16,
        marginBottom: 16,
    },
    sectionTitle: { fontSize: 13, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 12px' },
    row: { display: 'flex', gap: 16, alignItems: 'flex-start' },
    col: { flex: 1, minWidth: 0 },
    label: { fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 },
    input: {
        width: '100%',
        padding: '8px 10px',
        fontSize: 13,
        border: '1px solid #d1d5db',
        borderRadius: 6,
        background: '#fff',
        boxSizing: 'border-box',
    },
    textarea: {
        width: '100%',
        padding: '8px 10px',
        fontSize: 12,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        border: '1px solid #d1d5db',
        borderRadius: 6,
        background: '#fff',
        boxSizing: 'border-box',
        minHeight: 60,
        resize: 'vertical',
    },
    button: {
        padding: '8px 14px',
        fontSize: 13,
        fontWeight: 500,
        color: '#fff',
        background: '#0088b8',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
    },
    buttonSecondary: {
        padding: '8px 14px',
        fontSize: 13,
        fontWeight: 500,
        color: '#0088b8',
        background: '#fff',
        border: '1px solid #0088b8',
        borderRadius: 6,
        cursor: 'pointer',
    },
    meta: { fontSize: 12, color: '#6b7280', marginTop: 8 },
    urlBlock: {
        width: '100%',
        boxSizing: 'border-box',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: 12,
        lineHeight: 1.5,
        color: '#111827',
        background: '#f3f4f6',
        border: '1px solid #d1d5db',
        borderRadius: 6,
        padding: '10px 12px',
        wordBreak: 'break-all',
        whiteSpace: 'pre-wrap',
        resize: 'vertical',
        minHeight: 120,
    },
    error: { fontSize: 12, color: '#dc2626', marginTop: 8 },
    fixedList: { fontSize: 13, lineHeight: 1.7, color: '#374151', margin: 0, padding: 0, listStyle: 'none' },
    fixedKey: { color: '#6b7280', display: 'inline-block', minWidth: 110 },
    cardA: {
        background: '#fff',
        border: '1px solid #fca5a5',
        borderTop: '3px solid #dc2626',
        borderRadius: 8,
        padding: 16,
        flex: 1,
    },
    cardB: {
        background: '#fff',
        border: '1px solid #86efac',
        borderTop: '3px solid #16a34a',
        borderRadius: 8,
        padding: 16,
        flex: 1,
    },
    cardTitle: { fontSize: 14, fontWeight: 600, margin: '0 0 4px' },
    cardExpected: { fontSize: 12, color: '#4b5563', marginTop: 12, lineHeight: 1.5 },
    footer: { fontSize: 12, color: '#6b7280', marginTop: 16, textAlign: 'center' },
};

function MozioDemoPage() {
    const [ssoToken, setSsoToken] = useState('');
    const [expiresAt, setExpiresAt] = useState('');
    const [partnerTrackingId, setPartnerTrackingId] = useState('');
    const [currency, setCurrency] = useState('AUD');
    const [language, setLanguage] = useState('en-us');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    async function mint() {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${apiURL2}/mozio-demo/sso-token`);
            if (!res.ok) throw new Error(`Mint failed (${res.status})`);
            const body = await res.json();
            setSsoToken(body.ssoToken || '');
            setExpiresAt(body.expiresAt || '');
            setPartnerTrackingId(body.partnerTrackingId || '');
        } catch (e) {
            setError(String(e.message || e));
        } finally {
            setLoading(false);
        }
    }

    const urlA = useMemo(
        () => buildDeeplink({ ssoToken, currency, language, partnerTrackingId }),
        [ssoToken, currency, language, partnerTrackingId]
    );
    const urlB = useMemo(
        () => buildDeeplink({ ssoToken: '', currency, language, partnerTrackingId }),
        [currency, language, partnerTrackingId]
    );

    const [urlAEdit, setUrlAEdit] = useState(urlA);
    const [urlBEdit, setUrlBEdit] = useState(urlB);

    // Re-sync edited URLs whenever the form-derived URL changes; ad-hoc edits
    // are intentionally one-shot until the next form change.
    useEffect(() => { setUrlAEdit(urlA); }, [urlA]);
    useEffect(() => { setUrlBEdit(urlB); }, [urlB]);

    const tokenReady = ssoToken.trim().length > 0;

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <h1 style={styles.h1}>Mozio whitelabel — SSO + currency reproduction</h1>
                <p style={styles.intro}>
                    Two deeplinks below differ only in the <code>sso_token</code> param. The trip,{' '}
                    <code>partner_tracking_id</code>, <code>currency</code>, language, and all other
                    params are identical. Open both, compare what currency Mozio renders. Observed:
                    the SSO variant ignores <code>currency=</code> from the URL and falls back to
                    the agent profile currency; the no-SSO variant honors the URL.
                </p>
                <p style={{ ...styles.intro, color: '#b45309', fontWeight: 500 }}>
                    Tip: open each URL in an <strong>incognito / private window</strong> to avoid
                    residual Mozio session cookies skewing the result.
                </p>

                <div style={styles.section}>
                    <p style={styles.sectionTitle}>1. SSO token</p>
                    <button style={styles.button} onClick={mint} disabled={loading}>
                        {loading ? 'Minting…' : 'Mint fresh sso_token (sherryfq_@hotmail.com)'}
                    </button>
                    <textarea
                        style={{ ...styles.textarea, marginTop: 12 }}
                        placeholder="Paste sso_token manually if you have one, or click the button above."
                        value={ssoToken}
                        onChange={(e) => setSsoToken(e.target.value)}
                    />
                    {expiresAt && (
                        <div style={styles.meta}>
                            Cache expires: {expiresAt} · Mozio token lifetime is ~3 minutes.
                        </div>
                    )}
                    {partnerTrackingId && (
                        <div style={styles.meta}>partner_tracking_id: {partnerTrackingId}</div>
                    )}
                    {error && <div style={styles.error}>{error}</div>}
                </div>

                <div style={styles.section}>
                    <p style={styles.sectionTitle}>2. Variables &amp; fixed trip</p>
                    <div style={styles.row}>
                        <div style={styles.col}>
                            <label style={styles.label}>Currency</label>
                            <select
                                style={styles.input}
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                            >
                                {CURRENCIES.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                            <label style={{ ...styles.label, marginTop: 12 }}>Language</label>
                            <select
                                style={styles.input}
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                            >
                                {LANGUAGES.map((l) => (
                                    <option key={l} value={l}>
                                        {l}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.col}>
                            <label style={styles.label}>Fixed trip (identical in both URLs)</label>
                            <ul style={styles.fixedList}>
                                <li>
                                    <span style={styles.fixedKey}>Pickup:</span> {FIXED_TRIP.pickup}
                                </li>
                                <li>
                                    <span style={styles.fixedKey}>Dropoff:</span> {FIXED_TRIP.dropoff}
                                </li>
                                <li>
                                    <span style={styles.fixedKey}>Date/time:</span>{' '}
                                    {FIXED_TRIP.pickupDatetime}
                                </li>
                                <li>
                                    <span style={styles.fixedKey}>Passengers:</span>{' '}
                                    {FIXED_TRIP.numPassengers} · One-way
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <div style={styles.row}>
                    <div style={styles.cardA}>
                        <p style={styles.cardTitle}>A. WITH sso_token</p>
                        <textarea
                            style={styles.urlBlock}
                            value={urlAEdit}
                            onChange={(e) => setUrlAEdit(e.target.value)}
                        />
                        <button
                            style={{ ...styles.buttonSecondary, marginTop: 8 }}
                            onClick={() => window.open(urlAEdit, '_blank', 'noopener')}
                            disabled={!tokenReady}
                        >
                            Open in new tab
                        </button>
                        <p style={styles.cardExpected}>
                            <strong>Observed:</strong> Mozio rewrites the URL to{' '}
                            <code>currency=</code> the agent profile currency and renders prices in
                            that currency, ignoring the <code>currency</code> we passed.
                        </p>
                    </div>
                    <div style={styles.cardB}>
                        <p style={styles.cardTitle}>B. WITHOUT sso_token</p>
                        <textarea
                            style={styles.urlBlock}
                            value={urlBEdit}
                            onChange={(e) => setUrlBEdit(e.target.value)}
                        />
                        <button
                            style={{ ...styles.buttonSecondary, marginTop: 8 }}
                            onClick={() => window.open(urlBEdit, '_blank', 'noopener')}
                        >
                            Open in new tab
                        </button>
                        <p style={styles.cardExpected}>
                            <strong>Observed:</strong> Mozio honors <code>currency=</code> from the
                            URL and renders prices in that currency.
                        </p>
                    </div>
                </div>

                <p style={styles.footer}>
                    If card A opens to a Mozio login page, the sso_token has expired (~3 min) — mint
                    a fresh one and try again.
                </p>
            </div>
        </div>
    );
}

export default MozioDemoPage;
